/**
 * @file Component to assemble components for showing a selected entity for editing.
 * @author Tom Verschoore de la Houssaije
 */
import * as tslib_1 from "tslib";
import { EventEmitter, OnDestroy } from '@angular/core';
import { FormBase } from '../../base/form/form.base';
import { AbstractView } from '../../base/helpers/abstract-view';
import { RelationContainerComponent } from '../relation-container/relation-container.component';
import { RoutesService, ROUTE_PARAM_ID } from '../../routes.service';
var DetailBase = /** @class */ (function (_super) {
    tslib_1.__extends(DetailBase, _super);
    /**
     * Constructs a DetailComponent
     * @param configurationsService Service to get the configuration for this component.
     * @param router The angular router enabling navigation between the different form modes.
     * @param route The active route.
     * @param dialogService The dialog service.
     * @param alertsService The alert service.
     * @param translateService The translation service.
     */
    function DetailBase(userService, router, route, breezeService, dialogService, alertsService, settingsService, translateService) {
        var _this = _super.call(this, router, route, breezeService, dialogService, alertsService, translateService) || this;
        _this.userService = userService;
        _this.breezeService = breezeService;
        _this.dialogService = dialogService;
        _this.alertsService = alertsService;
        _this.settingsService = settingsService;
        /**
         * Gets, sets the value of the overlayMode property.
         * @private
         */
        _this._overlayMode = false;
        /** List of configutation to display entity relations for this detail */
        _this.relations = [];
        /**
         * Gets, sets the key of the entity shown by this detail.
         */
        _this.entityKey = undefined;
        /**
         * Event notifying that the detail is closing. Is used in the overlay.
         * @type {EventEmitter<string>}
         */
        _this.closing = new EventEmitter();
        if (_this.overlayMode) {
            return _this;
        }
        _this.subscriptionPool.add(_this.route.params.subscribe(function (params) {
            if (_this.overlayMode) {
                return;
            }
            var test = params[ROUTE_PARAM_ID];
            if (test == null) {
                return;
            }
            _this.entityKey = test;
        }));
        return _this;
    }
    Object.defineProperty(DetailBase.prototype, "overlayMode", {
        /**
         * Sets the flag indicating whether or not the component is shown in the overlay.
         * @readonly
         * @type {boolean}
         */
        get: function () {
            return this._overlayMode;
        },
        /**
         * Sets the flag indicating whether or not the component is shown in the overlay.
         */
        set: function (value) {
            this._overlayMode = value;
            if (this._overlayMode) {
                this.breezeService.nextDataContext();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DetailBase.prototype, "editMode", {
        /**
         * Gets the edit mode of the shown detail.
         * @returns true when the entity is changed.
         */
        get: function () {
            if (this.dataForm) {
                return this.dataForm.editMode;
            }
            return false;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DetailBase.prototype, "createMode", {
        /**
         * Gets the create mode of the shown detail.
         * New entities will be removed on view closing when there are no other changes and createMode is true.
         * @see AbstractView.checkChangesBeforeDeactivate
         * @readonly
         * @type {boolean} Is True when in create mode.
         */
        get: function () {
            if (this.dataForm == null) {
                // If there is no dataForm then there is also no relation.
                return false;
            }
            if (this.dataForm.hasChanges) {
                // The user will be asked if he wants to leave the view.
                return false;
            }
            if (this.relationLists.find(function (item) { return item.hasChanges; }) != null) {
                // The user will be asked if he wants to leave the view.
                return false;
            }
            if (this.relationLists.find(function (item) { return item.createMode; }) != null) {
                return true;
            }
            return this.dataForm.createMode;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Lifecycle hook that is called when the detail component is destroyed.
     */
    DetailBase.prototype.ngOnDestroy = function () {
        _super.prototype.ngOnDestroy.call(this);
        if (this.overlayMode) {
            this.breezeService.previousDataContext();
        }
    };
    /**
     * Used to test if the component can be deactived.
     * @returns {(Observable<boolean> | boolean)} True when there are no changes to save.
     */
    DetailBase.prototype.canDeactivate = function () {
        return this.checkChangesBeforeDeactivate(true);
    };
    /**
     * Saves entity changes made.
     * @param event The HTML event argument.
     */
    DetailBase.prototype.saveItem = function (event) {
        this.dataForm.saveItem(event);
        this.relationLists.forEach(function (relation) {
            relation.formSave(event);
        });
    };
    /**
     * Creates a new entity.
     * @param event The HTML event argument.
     */
    DetailBase.prototype.createItem = function (event) {
        this.dataForm.createItem(event);
    };
    /**
     * Cancels the last not saved entity changes made.
     */
    DetailBase.prototype.cancelItem = function (event) {
        // This cancels only main detail form.
        // Not relations forms.
        // this.dataForm.cancelItem();
        this.breezeService.rejectChanges();
        this.relationLists.forEach(function (relation) {
            relation.formCancel(event);
        });
        this.dataForm.close();
    };
    /**
     * Removes the shown entity. A removed entity will be deleted permanently after saving the changes.
     * @param event The HTML event argument.
     */
    DetailBase.prototype.removeItem = function (event) {
        this.dataForm.removeItem(event);
        this.relationLists.forEach(function (relation) {
            relation.formDelete(event);
        });
    };
    Object.defineProperty(DetailBase.prototype, "hasComments", {
        get: function () {
            if (this.settingsService.settings.comments !== undefined && this.settingsService.settings.comments === false) { // Fix for #4165
                return false;
            }
            if (!this.configuration || !this.configuration.features) {
                return false;
            }
            return this.configuration.features.findIndex(function (feature) { return feature === 'Comments'; }) > -1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DetailBase.prototype, "hasAudit", {
        get: function () {
            if (this.settingsService.settings.audittrail !== undefined && this.settingsService.settings.audittrail === false) { // Fix for #4165
                return false;
            }
            if (!this.configuration || !this.configuration.features) {
                return false;
            }
            return this.configuration.features.findIndex(function (feature) { return feature === 'Audit'; }) > -1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DetailBase.prototype, "hasFiles", {
        get: function () {
            if (this.settingsService.settings.files !== undefined && this.settingsService.settings.files === false) { // Fix for #4184
                return false;
            }
            if (!this.configuration || !this.configuration.features) {
                return false;
            }
            return this.configuration.features.findIndex(function (feature) { return feature === 'Files'; }) > -1;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Method to notify the component of a successful entity deletion.
     * @param result The returned response of a entity deletion.
     */
    DetailBase.prototype.notifyRemoval = function (result) {
        this.closing.emit(this.entityKey);
        if (this.overlayMode) {
            return;
        }
        // One at a time.
        this.navigateBackFrom(result.entities[0], 'delete-result');
    };
    /**
     * Method to notify the component of a successful change commit.
     * @param result The returned response of a entity change commit.
     */
    DetailBase.prototype.notifySavement = function (result) {
        this.closing.emit(this.entityKey);
        if (this.overlayMode) {
            return;
        }
        var path = this.router.url;
        var i = path.lastIndexOf('/new');
        if (i > 0 && result && result.entities.length > 0) {
            this.navigateBackFrom(result.entities[0], 'new-result');
        }
    };
    /**
     * Method to notify the component of a change cancellation.
     * @param item The changed entity
     */
    DetailBase.prototype.notifyCancellation = function (item) {
        this.closing.emit(this.entityKey);
        if (this.overlayMode) {
            return;
        }
        var path = this.router.url; // this.location.path();
        var i = path.lastIndexOf('/new');
        if (i > 0 && item) {
            this.navigateBackFrom(item, 'delete-result');
        }
    };
    /**
     * Actions are only allowed when the current user is the owner of the shown entity.
     * The method checks also if actions are defined in the configuration.
     * @param {Entity} item The entity shown.
     * @returns {boolean} True when the user is the owner of the shown entity otherwise false.
     */
    DetailBase.prototype.actionsAllowed = function (item) {
        var _this = this;
        if (!this.configuration.actions || !item) {
            return false;
        }
        if (this.configuration.actions.every(function (action) { return !_this.userService.isActionAllowed(action); })) {
            return false;
        }
        var entity = item;
        // Demands and services have to be allowed for the accept buttons on the marketplace.
        // Tasks, shift and their templates have to be allowed for the copy buttons.
        if (entity.entityType.shortName === 'Demand' || entity.entityType.shortName === 'Service' ||
            entity.entityType.shortName === 'Task' || entity.entityType.shortName === 'TaskTemplate' ||
            entity.entityType.shortName === 'Shift' || entity.entityType.shortName === 'ShiftTemplate' ||
            entity.entityType.shortName === 'Function') {
            return true;
        }
        // Actions directly on entities are always allowed (only User.enable so far)
        if (this.configuration.actions.some(function (action) { return action.method != null; })) {
            return true;
        }
        if (entity.entityType.shortName === 'PlanItem') {
            entity = entity['Item'];
        }
        var resourceOid = entity['Resource_Oid'];
        if (!resourceOid) {
            return false;
        }
        if (this.userService.user.Resource_Oid !== resourceOid) {
            return false;
        }
        return true;
    };
    /**
     * Override of the default behavior.
     * Deletion of entities is not supported in the overlay mode.
     * @protected
     * @returns {boolean} True when displayed entity can be deleted.
     */
    DetailBase.prototype.getCanDelete = function () {
        if (this.overlayMode) {
            return false;
        }
        return _super.prototype.getCanDelete.call(this);
    };
    /**
     * Is called after the block name is set and the configuration is loaded.
     * Override this method to process the loaded configuration data.
     */
    DetailBase.prototype.afterInitialize = function () {
        var _this = this;
        // Get the components
        var components = Object.create(this.configuration.components);
        // Check the type of the component to handle specific situations
        components.forEach(function (component) {
            switch (component.component) {
                case 'InsightsComponent': {
                    _this.insights = component;
                    break;
                }
                case 'FormComponent': {
                    _this.details = component;
                    break;
                }
                case 'ForecastRelationComponent':
                case 'RelationComponent': {
                    _this.relations.push(component);
                    break;
                }
                default: {
                    throw new Error('Unknown component (' + component.component + ')');
                }
            }
        });
    };
    /**
     * Notifies the main view of a new entity or a deletion.
     * @param item The entity deleted.
     * @param path The current path root.
     * @param action The action for a new or a deleted result
     */
    DetailBase.prototype.navigateBackFrom = function (item, action) {
        var _this = this;
        var id = '';
        item.entityType.keyProperties.forEach(function (property) {
            id += '_' + item[property.name];
        });
        id = id.substring(1);
        var queryParams = {};
        Object.keys(this.route.snapshot.queryParams).forEach(function (key) {
            queryParams[key] = _this.route.snapshot.queryParams[key];
        });
        queryParams[action] = id;
        RoutesService.navigateBack(this.router, this.route, queryParams);
    };
    return DetailBase;
}(AbstractView));
export { DetailBase };
